/**
 * Composable for form validation across components
 * Provides consistent validation rules and error handling
 */

export const useFormValidation = () => {
  /**
   * Validation rules for common form fields
   */
  const validationRules = {
    required: (value) => {
      if (typeof value === 'string') {
        return value.trim() !== '' ? null : 'This field is required';
      }
      return value ? null : 'This field is required';
    },

    email: (value) => {
      if (!value) return null;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value) ? null : 'Please enter a valid email address';
    },

    phone: (value) => {
      if (!value) return null;
      const phoneRegex = /^[\+]?[1-9]?[\s\-\(\)\d]{7,20}$/;
      return phoneRegex.test(value) ? null : 'Please enter a valid phone number';
    },

    url: (value) => {
      if (!value) return null;
      try {
        new URL(value);
        return null;
      } catch {
        return 'Please enter a valid URL';
      }
    },

    minLength: (min) => (value) => {
      if (!value) return null;
      return value.length >= min ? null : `Must be at least ${min} characters`;
    },

    maxLength: (max) => (value) => {
      if (!value) return null;
      return value.length <= max ? null : `Must be no more than ${max} characters`;
    },

    numberRange: (min, max) => (value) => {
      if (!value) return null;
      const num = Number(value);
      if (isNaN(num)) return 'Must be a valid number';
      if (num < min) return `Must be at least ${min}`;
      if (num > max) return `Must be no more than ${max}`;
      return null;
    },

    futureDate: (value) => {
      if (!value) return null;
      const date = new Date(value);
      const now = new Date();
      return date > now ? null : 'Date must be in the future';
    },

    custom: (validator, message) => (value) => {
      return validator(value) ? null : message;
    }
  };

  /**
   * Validate a single field
   * @param {*} value - Field value
   * @param {Array|Function} rules - Validation rules to apply
   * @returns {string|null} Error message or null if valid
   */
  const validateField = (value, rules) => {
    if (!rules) return null;

    const rulesArray = Array.isArray(rules) ? rules : [rules];

    for (const rule of rulesArray) {
      let validationRule;
      let params = [];

      if (typeof rule === 'string') {
        validationRule = validationRules[rule];
      } else if (typeof rule === 'function') {
        validationRule = rule;
      } else if (rule.type && validationRules[rule.type]) {
        validationRule = validationRules[rule.type];
        params = rule.params || [];
      }

      if (validationRule) {
        const error = validationRule(value, ...params);
        if (error) return error;
      }
    }

    return null;
  };

  /**
   * Validate entire form
   * @param {Object} formData - Form data object
   * @param {Object} schema - Validation schema
   * @returns {Object} Object with errors and isValid flag
   */
  const validateForm = (formData, schema) => {
    const errors = {};
    let isValid = true;

    for (const [field, rules] of Object.entries(schema)) {
      const error = validateField(formData[field], rules);
      if (error) {
        errors[field] = error;
        isValid = false;
      }
    }

    return { errors, isValid };
  };

  /**
   * Common validation schemas for different form types
   */
  const commonSchemas = {
    contact: {
      name: ['required', 'minLength:2'],
      email: ['required', 'email'],
      phone: ['phone'],
      company: ['minLength:2'],
      message: ['required', 'minLength:10']
    },

    quoteRequest: {
      name: ['required', 'minLength:2'],
      email: ['required', 'email'],
      phone: ['phone'],
      company: ['minLength:2'],
      quantity: ['required', 'numberRange:1,100'],
      delivery_location: ['required', 'minLength:5'],
      delivery_date: ['futureDate'],
      message: ['required', 'minLength:10']
    },

    inquiry: {
      name: ['required', 'minLength:2'],
      email: ['required', 'email'],
      phone: ['phone'],
      company: ['minLength:2'],
      inquiry_type: ['required'],
      message: ['required', 'minLength:10']
    }
  };

  /**
   * Create a reactive form validator
   * @param {Object} schema - Validation schema
   * @returns {Object} Reactive validator object
   */
  const createFormValidator = (schema) => {
    const errors = ref({});
    const touched = ref({});

    const validate = (formData) => {
      const result = validateForm(formData, schema);
      errors.value = result.errors;
      return result.isValid;
    };

    const validateField = (field, value) => {
      const fieldRules = schema[field];
      if (!fieldRules) return null;

      const error = validateField(value, fieldRules);
      errors.value[field] = error;
      return error;
    };

    const markFieldTouched = (field) => {
      touched.value[field] = true;
    };

    const isFieldValid = (field) => {
      return !errors.value[field];
    };

    const isFieldTouched = (field) => {
      return touched.value[field];
    };

    const clearErrors = () => {
      errors.value = {};
      touched.value = {};
    };

    const hasErrors = computed(() => Object.keys(errors.value).length > 0);

    return {
      errors: readonly(errors),
      touched: readonly(touched),
      validate,
      validateField,
      markFieldTouched,
      isFieldValid,
      isFieldTouched,
      clearErrors,
      hasErrors
    };
  };

  return {
    validateField,
    validateForm,
    
    commonSchemas,
    
    createFormValidator,
    
    validationRules
  };
};
